import { feedApi } from './client';
import type { Task, Event, ParsedIntent, TrackingIntent, TaskCreationJobResponse } from '@/types';

export const taskService = {
  async parseIntent(text: string, userLanguage?: string): Promise<TrackingIntent> {
    const response = await feedApi.post<TrackingIntent>('/tasks/parse-intent', {
      input: text,
      user_language: userLanguage,
    });
    return response.data;
  },

  async createFromIntent(input: string, isPublic: boolean = false, selectedSources?: string[]): Promise<Task> {
    const response = await feedApi.post<Task>('/tasks/create-from-intent', {
      input,
      is_public: isPublic,
      selected_sources: selectedSources,
    });
    return response.data;
  },

  // @deprecated - No longer needed, backend returns task immediately
  async getTaskCreationJob(jobId: string): Promise<TaskCreationJobResponse> {
    const response = await feedApi.get<TaskCreationJobResponse>(`/tasks/jobs/${jobId}`);
    return response.data;
  },

  async createTask(data: {
    intent: ParsedIntent;
    title?: string;
    description?: string;
  }): Promise<Task> {
    const response = await feedApi.post<Task>('/tasks', data);
    return response.data;
  },

  async getTasks(params?: { status_filter?: string }): Promise<Task[]> {
    const response = await feedApi.get<Task[]>('/tasks', { params });
    return response.data;
  },

  async getTask(id: string): Promise<Task> {
    const response = await feedApi.get<Task>(`/tasks/${id}`);
    return response.data;
  },

  async updateTask(
    id: string,
    data: Partial<Pick<Task, 'status' | 'title' | 'description' | 'is_public'>>
  ): Promise<Task> {
    const response = await feedApi.patch<Task>(`/tasks/${id}`, data);
    return response.data;
  },

  async deleteTask(id: string): Promise<void> {
    await feedApi.delete(`/tasks/${id}`);
  },

  async getPublicTasks(params?: { limit?: number; search?: string }): Promise<Task[]> {
    if (params?.search) {
      const response = await feedApi.get<Task[]>('/tasks/search', {
        params: { q: params.search, limit: params.limit },
      });
      return response.data;
    }

    const response = await feedApi.get<Task[]>('/tasks/public', {
      params,
    });
    return response.data;
  },

  async subscribeToTask(taskId: string): Promise<void> {
    await feedApi.post(`/tasks/${taskId}/subscribe`);
  },

  async unsubscribeFromTask(taskId: string): Promise<void> {
    await feedApi.delete(`/tasks/${taskId}/subscribe`);
  },

  async getTaskEvents(taskId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<Event[]> {
    const response = await feedApi.get<Event[]>(`/tasks/${taskId}/events`, {
      params: {
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
      },
    });
    return response.data;
  },
};

export default taskService;
